import * as React from 'react';

import { createEditorValue, isDocumentEmpty, trimDocument } from '@gitbook/doc-core';
import type { Document } from '@gitbook/slate';
import { Button, LoadingIndicator } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useCurrentUser } from '../../state';
import { RichTextarea, useBasicEditorPersistedValue } from '../BasicEditor';
import { UserAvatar } from '../Users';

/** The target of the comment (node or page) */
export type CommentTarget = string | undefined;

export interface CommentInputProps {
    /**
     * The test ID of the input.
     */
    testID: string;

    /**
     * The element that the user is commenting on.
     */
    targetNode: CommentTarget;

    /**
     * The placeholder of the input.
     */
    placeholder: string;

    /**
     * The callback to submit the comment.
     */
    onSubmit: (document: Document) => Promise<void>;

    /**
     * The callback to cancel the input.
     */
    onCancel?: () => void;

    /**
     * The maximum number of rows to display.
     * @default 4
     */
    maxRows?: number;
}

/**
 * Input to post a comment or reply, incl. avatar, editor, and buttons.
 */
export function CommentInput(props: CommentInputProps) {
    const { testID, targetNode, placeholder, onSubmit, onCancel, maxRows = 4 } = props;

    const { value, setValue } = useBasicEditorPersistedValue(`comment:${targetNode}`);
    const [loading, wrapLoading] = useLoadingState();
    const user = useCurrentUser();

    const isEmpty = React.useMemo(() => {
        return isDocumentEmpty(trimDocument(value.document));
    }, [value.document]);

    // Auto-focus on mount when requested
    React.useEffect(() => {
        setValue((current) => current.change().collapseToEndOf(current.document).focus().value);
    }, [setValue]);

    const onReset = (cancel = true) => {
        setValue(createEditorValue('', 'basic'));
        if (cancel && onCancel) {
            onCancel();
        }
    };

    const handleSubmit = () => {
        if (isEmpty) {
            return;
        }

        wrapLoading(async () => {
            await onSubmit(trimDocument(value.document));
            onReset(false);
        });
    };

    return (
        <div className="flex w-full items-start gap-2" data-testid={testID}>
            <UserAvatar user={user} size="sm" className="mt-[5px]" />
            <RichTextarea
                value={value}
                onChange={setValue}
                placeholder={placeholder}
                maxRows={maxRows}
                withUserMentions
                withAgentMentions
                withEmojis
                withPageMentions
                insertButtonsSize="small"
                onSubmit={handleSubmit}
                submitButton={
                    <Button
                        leadingIcon={loading ? LoadingIndicator : null}
                        kind="primary"
                        size="small"
                        disabled={loading || isEmpty}
                        label="Submit"
                        onPress={handleSubmit}
                        tooltip={{ shortcut: 'Enter', label: 'Submit comment' }}
                    />
                }
            />
        </div>
    );
}
